import { ProductSearchResult } from "@/api/product_catalog"
import { Promotion, PromotionLineData } from "./types"
import { promotion_type_layout } from "./promotion_type_layout"

/**
 * The arithmetic behind a promotion's deal terms, in one place: what a deal costs per unit, what
 * that comes to over the planned volume, how units and cases relate, and what the deal does to
 * margin. Everything here is pure; money is done in cents to avoid float noise.
 *
 * The regular price is what turns a percentage, a Multi-Buy ("2 for $6") or a BOGO into dollars.
 * Without it those deals have no estimate (1st Phorm: "Discount % can't produce an estimate on its
 * own. There's no regular price or product price to apply it to").
 */

/** The fields the deal math reads; a `Promotion` satisfies it, as does a form draft. */
export type DealTerms = Pick<
  Promotion,
  | "promotion_type"
  | "discount_amount"
  | "discount_percentage"
  | "regular_price"
  | "multibuy_quantity"
  | "multibuy_price"
  | "bogo_buy_quantity"
  | "bogo_get_quantity"
  | "bogo_free_unit_cost"
>

function cents(amount: number): number {
  return Math.round(amount * 100)
}

function dollars(amount_cents: number): number {
  return amount_cents / 100
}

function positive(value: number | undefined | null): number | undefined {
  return value != null && Number.isFinite(value) && value > 0 ? value : undefined
}

/** How a per-unit cost was arrived at, so the form can show its working. */
export interface UnitDiscount {
  /** Dollars the deal costs per unit sold on it. */
  amount: number
  /** Short working, e.g. "15% of $3.99". Empty when the $ was entered directly. */
  working: string
}

/** PURE: dollars off the regular price for a percentage, rounded to the cent. */
export function percent_of_price(regular_price: number, pct: number): number {
  return dollars(Math.round(cents(regular_price) * pct / 100))
}

/**
 * PURE: what the deal costs per unit sold on it, by the type's deal fields.
 *
 * - discount: the $ per unit as entered; failing that, the % of the regular price.
 * - multibuy: regular price less the deal price per unit (2 for $6 against $3.99 is $0.99 off each).
 * - bogo: the free units' cost spread over every unit sold on the deal — buy 1 get 1 at $3.99 a
 *   free unit is $2.00 per unit sold (one free for every two).
 * - none (fees, slotting, accruals): no per-unit cost.
 */
export function unit_discount(terms: DealTerms): UnitDiscount | null {
  const layout = promotion_type_layout(terms.promotion_type)
  const regular_price = positive(terms.regular_price)
  switch (layout.deal_fields) {
    case "discount": {
      const amount = positive(terms.discount_amount)
      if (amount) return { amount, working: "" }
      const pct = positive(terms.discount_percentage)
      if (pct && regular_price) {
        return { amount: percent_of_price(regular_price, pct), working: `${format_pct(pct)} of ${format_money(regular_price)}` }
      }
      return null
    }
    case "multibuy": {
      const quantity = positive(terms.multibuy_quantity)
      const price = positive(terms.multibuy_price)
      if (!quantity || !price || !regular_price) return null
      const deal_each = cents(price) / quantity
      const off = Math.max(0, Math.round(cents(regular_price) - deal_each))
      return {
        amount: dollars(off),
        working: `${quantity} for ${format_money(price)} against ${format_money(regular_price)} each`,
      }
    }
    case "bogo": {
      const buy = positive(terms.bogo_buy_quantity)
      const get = positive(terms.bogo_get_quantity)
      const free_unit_cost = positive(terms.bogo_free_unit_cost) ?? regular_price
      if (!buy || !get || !free_unit_cost) return null
      const per_unit = Math.round(cents(free_unit_cost) * get / (buy + get))
      return {
        amount: dollars(per_unit),
        working: `buy ${buy} get ${get} free at ${format_money(free_unit_cost)} a unit`,
      }
    }
    case "none":
      return null
  }
}

/** PURE: the shelf price on the deal, when a regular price and a unit discount are both known. */
export function promoted_price(terms: DealTerms): number | null {
  const regular_price = positive(terms.regular_price)
  const discount = unit_discount(terms)
  if (!regular_price || !discount) return null
  return dollars(Math.max(0, cents(regular_price) - cents(discount.amount)))
}

/** Participation as a 0–1 share; blank is treated as every store (1). */
export function participation_share(participation_pct: number | undefined): number {
  if (participation_pct == null || !Number.isFinite(participation_pct)) return 1
  return Math.min(Math.max(participation_pct, 0), 100) / 100
}

/** PURE: unit cost × units × participation, in cents, as dollars. */
export function trade_spend(unit_cost: number, units: number, participation_pct: number | undefined): number {
  return dollars(Math.round(cents(unit_cost) * units * participation_share(participation_pct)))
}

/** PURE: units ↔ cases through a case pack. Null when either side is unknown. */
export function cases_from_units(units: number | undefined, units_per_case: number | undefined): number | null {
  const pack = positive(units_per_case)
  if (units == null || !Number.isFinite(units) || !pack) return null
  return Math.round((units / pack) * 100) / 100
}

export function units_from_cases(cases: number | undefined, units_per_case: number | undefined): number | null {
  const pack = positive(units_per_case)
  if (cases == null || !Number.isFinite(cases) || !pack) return null
  return Math.round(cases * pack)
}

/**
 * PURE: the one case pack every picked product shares, or null when they differ (then the lines
 * carry their own) or none is known.
 */
export function shared_units_per_case(products: Array<Pick<ProductSearchResult, "units_per_case">>): number | null {
  const packs = new Set(products.map((product) => positive(product.units_per_case)).filter((pack): pack is number => pack != null))
  if (packs.size !== 1) return null
  return [...packs][0]
}

/**
 * PURE: whether the units and cases typed disagree with the case pack by more than rounding —
 * 10,000 units and 500 cases at 20 per case agree; 10,000 and 400 do not.
 */
export function volumes_disagree(units: number | undefined, cases: number | undefined, units_per_case: number | undefined): boolean {
  const expected_cases = cases_from_units(units, units_per_case)
  if (expected_cases == null || cases == null || !Number.isFinite(cases)) return false
  return Math.abs(expected_cases - cases) > Math.max(1, expected_cases * 0.01)
}

/** The price a deal is taken off, and where it came from, for the help text. */
export interface SuggestedPrice {
  amount: number
  source: string
}

/** PURE: the current customer price for a product, by customer then retailer name, if any is in force today. */
export function current_customer_price(
  product: Pick<ProductSearchResult, "prices">,
  customer: string | undefined,
  retailer: string | undefined,
  today: string,
): number | null {
  const in_force = (product.prices ?? []).filter(
    (price) => (!price.effective_start || price.effective_start <= today) && (!price.effective_end || price.effective_end >= today),
  )
  const same = (a: string | undefined | null, b: string | undefined) => Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase())
  const by_retailer = in_force.find((price) => same(price.retailer_name, retailer) || same(price.customer_name, retailer))
  const by_customer = in_force.find((price) => same(price.customer_name, customer))
  const pick = by_retailer ?? by_customer
  return pick ? pick.unit_price : null
}

/**
 * PURE: the regular price to suggest for a product — its shelf price when the catalog has one,
 * else the price in force for this customer, else the list price spread over the case. Null when
 * the catalog knows none of those.
 */
export function suggested_regular_price(
  product: Pick<ProductSearchResult, "shopping_price" | "list_price" | "units_per_case" | "prices">,
  customer: string | undefined,
  retailer: string | undefined,
  today: string,
): SuggestedPrice | null {
  const shelf = positive(product.shopping_price)
  if (shelf) return { amount: shelf, source: "catalog shelf price" }
  const customer_price = current_customer_price(product, customer, retailer, today)
  if (customer_price) return { amount: customer_price, source: `your price to ${retailer || customer || "this customer"}` }
  const list = positive(product.list_price)
  const pack = positive(product.units_per_case)
  if (list && pack) return { amount: dollars(Math.round(cents(list) / pack)), source: "catalog list price per unit" }
  return null
}

/** PURE: the catalog cost per unit, when the catalog carries a cost of goods (per case). */
export function suggested_unit_cost(product: Pick<ProductSearchResult, "cogs" | "units_per_case">): number | null {
  const cogs = positive(product.cogs)
  const pack = positive(product.units_per_case)
  if (!cogs) return null
  return dollars(Math.round(cents(cogs) / (pack ?? 1)))
}

/** What a deal does to the brand's margin on one unit. */
export interface MarginImpact {
  regular_margin: number
  regular_margin_pct: number
  promoted_margin: number
  promoted_margin_pct: number
  /** Margin given up per unit — the unit discount. */
  per_unit: number
}

/**
 * PURE: margin before and after the deal, from the regular price, the cost per unit and the deal's
 * cost per unit. The brand funds the discount, so promoted margin is regular margin less the unit
 * cost of the deal. Null until price, cost and a unit discount are all known.
 */
export function margin_impact(regular_price: number | undefined, unit_cost: number | undefined, unit_deal_cost: number | undefined): MarginImpact | null {
  const price = positive(regular_price)
  const cost = unit_cost != null && Number.isFinite(unit_cost) && unit_cost >= 0 ? unit_cost : undefined
  const deal = unit_deal_cost != null && Number.isFinite(unit_deal_cost) ? unit_deal_cost : undefined
  if (!price || cost == null || deal == null) return null
  const regular_margin = dollars(cents(price) - cents(cost))
  const promoted_margin = dollars(cents(price) - cents(cost) - cents(deal))
  return {
    regular_margin,
    regular_margin_pct: Math.round((regular_margin / price) * 1000) / 10,
    promoted_margin,
    promoted_margin_pct: Math.round((promoted_margin / price) * 1000) / 10,
    per_unit: deal,
  }
}

/** PURE: a line's cost per unit — its own $ or % off its own price, falling back to the header's. */
export function line_unit_discount(line: PromotionLineData, header: DealTerms): number | null {
  const amount = positive(line.discount_amount)
  if (amount) return amount
  const pct = positive(line.discount_percentage)
  const price = positive(line.regular_shopper_price)
  if (pct && price) return percent_of_price(price, pct)
  const from_header = unit_discount({ ...header, regular_price: price ?? header.regular_price })
  return from_header ? from_header.amount : null
}

/** PURE: a line's planned spend — its cost per unit × its planned units. */
export function line_spend(line: PromotionLineData, header: DealTerms): number | null {
  const discount = line_unit_discount(line, header)
  const units = positive(line.planned_volume_units)
  if (discount == null || !units) return null
  return trade_spend(discount, units, undefined)
}

/**
 * PURE: the lines as they are saved — each carrying the spend and the promoted price the form
 * worked out for it, so a line that leaves its discount blank (meaning "the header's") still
 * stores what it comes to. The header's own fields are not touched.
 */
export function lines_for_save(promotion: Pick<Promotion, "lines"> & DealTerms): PromotionLineData[] | undefined {
  if (!promotion.lines) return undefined
  return promotion.lines.map((line) => {
    const discount = line_unit_discount(line, promotion)
    const price = positive(line.regular_shopper_price)
    return {
      ...line,
      total_planned_spending: line_spend(line, promotion) ?? undefined,
      promoted_shopper_price: price != null && discount != null ? dollars(Math.max(0, cents(price) - cents(discount))) : line.promoted_shopper_price,
    }
  })
}

/** Sums over a promotion's lines, for the header and the summary. */
export interface LineTotals {
  units: number
  cases: number
  spend: number
  /** Lines that have units but no cost per unit yet, so the spend total is short by them. */
  lines_without_spend: number
}

export function line_totals(lines: PromotionLineData[], header: DealTerms): LineTotals {
  let units = 0
  let cases = 0
  let spend_cents = 0
  let lines_without_spend = 0
  for (const line of lines) {
    units += positive(line.planned_volume_units) ?? 0
    cases += positive(line.planned_volume_cases) ?? 0
    const spend = line_spend(line, header)
    if (spend == null) {
      if (positive(line.planned_volume_units)) lines_without_spend += 1
    } else {
      spend_cents += cents(spend)
    }
  }
  return { units, cases: Math.round(cases * 100) / 100, spend: dollars(spend_cents), lines_without_spend }
}

/** PURE: with lift already in the planned volume, how much of it is baseline and how much is lift. */
export function split_baseline_and_lift(planned_units: number | undefined, lift_pct: number | undefined): { baseline: number; lift: number } | null {
  const units = positive(planned_units)
  const lift = lift_pct != null && Number.isFinite(lift_pct) && lift_pct > 0 ? lift_pct : undefined
  if (!units || !lift) return null
  const baseline = Math.round(units / (1 + lift / 100))
  return { baseline, lift: units - baseline }
}

export function format_money(amount: number): string {
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Percent already in percent units: 35 → "35%", 12.5 → "12.5%". */
export function format_pct(pct: number): string {
  return `${+pct.toFixed(2)}%`
}

export function format_units(units: number): string {
  return `${Math.round(units).toLocaleString("en-US")} units`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("unit_discount", () => {
    it("takes the $ per unit as entered on a TPR", () => {
      expect(unit_discount({ promotion_type: "TPR", discount_amount: 1.5, discount_percentage: 15 })).toEqual({ amount: 1.5, working: "" })
    })
    it("turns a % into dollars once there is a regular price, and shows the working", () => {
      expect(unit_discount({ promotion_type: "TPR", discount_percentage: 15 })).toBeNull()
      expect(unit_discount({ promotion_type: "TPR", discount_percentage: 15, regular_price: 3.99 })).toEqual({ amount: 0.6, working: "15% of $3.99" })
    })
    it("works a Multi-Buy out against the regular price", () => {
      // Bakhrom's 2 for $6: nothing calculated before, because there was no regular price.
      expect(unit_discount({ promotion_type: "Multi-Buy", multibuy_quantity: 2, multibuy_price: 6 })).toBeNull()
      expect(unit_discount({ promotion_type: "Multi-Buy", multibuy_quantity: 2, multibuy_price: 6, regular_price: 3.99 })).toEqual({
        amount: 0.99,
        working: "2 for $6.00 against $3.99 each",
      })
      // A deal price above the regular price costs nothing.
      expect(unit_discount({ promotion_type: "Multi-Buy", multibuy_quantity: 2, multibuy_price: 9, regular_price: 3.99 })?.amount).toBe(0)
    })
    it("spreads a BOGO's free units over every unit sold on the deal", () => {
      expect(unit_discount({ promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1, regular_price: 3.99 })).toEqual({
        amount: 2,
        working: "buy 1 get 1 free at $3.99 a unit",
      })
      // Buy 2 get 1 at a stated free-unit cost: one of every three units is free.
      expect(unit_discount({ promotion_type: "BOGO", bogo_buy_quantity: 2, bogo_get_quantity: 1, bogo_free_unit_cost: 3, regular_price: 5 })?.amount).toBe(1)
      expect(unit_discount({ promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1 })).toBeNull()
    })
    it("has no per-unit cost on a fee", () => {
      expect(unit_discount({ promotion_type: "AD Fee - Signage", discount_amount: 1.5 })).toBeNull()
    })
  })

  describe("promoted_price", () => {
    it("is the regular price less the deal", () => {
      expect(promoted_price({ promotion_type: "TPR", discount_amount: 1.5, regular_price: 3.99 })).toBe(2.49)
      expect(promoted_price({ promotion_type: "TPR", discount_amount: 1.5 })).toBeNull()
    })
  })

  describe("units and cases", () => {
    it("convert through the case pack", () => {
      expect(cases_from_units(10_000, 20)).toBe(500)
      expect(units_from_cases(500, 20)).toBe(10_000)
      expect(cases_from_units(10_000, undefined)).toBeNull()
    })
    it("agree on one shared case pack and disagree on mixed ones", () => {
      expect(shared_units_per_case([{ units_per_case: 20 }, { units_per_case: 20 }])).toBe(20)
      expect(shared_units_per_case([{ units_per_case: 20 }, { units_per_case: 12 }])).toBeNull()
      expect(shared_units_per_case([{ units_per_case: undefined }])).toBeNull()
    })
    it("flags units and cases that do not match the pack", () => {
      // Bakhrom typed 10,000 units and 500 cases at 20 per case: consistent, no warning.
      expect(volumes_disagree(10_000, 500, 20)).toBe(false)
      expect(volumes_disagree(10_000, 400, 20)).toBe(true)
      expect(volumes_disagree(10_000, 500, undefined)).toBe(false)
    })
  })

  describe("suggested_regular_price", () => {
    const today = "2026-10-16"
    it("prefers the shelf price, then the customer's price, then list ÷ case", () => {
      expect(suggested_regular_price({ shopping_price: 3.99, list_price: 40, units_per_case: 20, prices: [] }, "KeHE", undefined, today)).toEqual({
        amount: 3.99,
        source: "catalog shelf price",
      })
      const priced = {
        list_price: 40,
        units_per_case: 20,
        prices: [
          { customer_name: "KeHE", unit_price: 2.1, currency: "USD" },
          { customer_name: "UNFI", unit_price: 2.3, currency: "USD", effective_end: "2026-01-01" },
        ],
      }
      expect(suggested_regular_price(priced, "KeHE", undefined, today)).toEqual({ amount: 2.1, source: "your price to KeHE" })
      // UNFI's price expired, so the list price per unit is what is left.
      expect(suggested_regular_price(priced, "UNFI", undefined, today)).toEqual({ amount: 2, source: "catalog list price per unit" })
      expect(suggested_regular_price({ prices: [] }, "KeHE", undefined, today)).toBeNull()
    })
    it("matches a retailer's price ahead of the distributor's", () => {
      const prices = [
        { customer_name: "KeHE", unit_price: 2.1, currency: "USD" },
        { customer_name: "KeHE", retailer_name: "Sprouts", unit_price: 2.5, currency: "USD" },
      ]
      expect(current_customer_price({ prices }, "KeHE", "Sprouts", today)).toBe(2.5)
      expect(current_customer_price({ prices }, "kehe", undefined, today)).toBe(2.1)
    })
  })

  describe("margin_impact", () => {
    it("shows margin before and after the brand funds the deal", () => {
      const impact = margin_impact(3.99, 1.5, 1.5)
      expect(impact).toEqual({ regular_margin: 2.49, regular_margin_pct: 62.4, promoted_margin: 0.99, promoted_margin_pct: 24.8, per_unit: 1.5 })
      expect(margin_impact(3.99, undefined, 1.5)).toBeNull()
    })
    it("reads the catalog's cost per case as cost per unit", () => {
      expect(suggested_unit_cost({ cogs: 30, units_per_case: 20 })).toBe(1.5)
      expect(suggested_unit_cost({ cogs: 30 })).toBe(30)
      expect(suggested_unit_cost({})).toBeNull()
    })
  })

  describe("lines", () => {
    const header: DealTerms = { promotion_type: "TPR", discount_amount: 1, regular_price: 3 }
    it("use their own terms, falling back to the header's", () => {
      expect(line_unit_discount({ discount_amount: 1.5 }, header)).toBe(1.5)
      expect(line_unit_discount({ discount_percentage: 10, regular_shopper_price: 5 }, header)).toBe(0.5)
      expect(line_unit_discount({}, header)).toBe(1)
      // A header % applies to the line's own price.
      expect(line_unit_discount({ regular_shopper_price: 5 }, { promotion_type: "TPR", discount_percentage: 10 })).toBe(0.5)
    })
    it("total up units, cases and spend, counting lines it could not price", () => {
      const totals = line_totals(
        [
          { discount_amount: 1.5, planned_volume_units: 10_000, planned_volume_cases: 500 },
          { planned_volume_units: 2_000, planned_volume_cases: 100 },
          { planned_volume_units: 300 },
        ],
        { promotion_type: "TPR", discount_percentage: 10 },
      )
      expect(totals).toEqual({ units: 12_300, cases: 600, spend: 15_000, lines_without_spend: 2 })
    })
  })

  describe("lines_for_save", () => {
    it("stores the spend and promoted price a blank-discount line inherits from the header", () => {
      const saved = lines_for_save({ promotion_type: "TPR", discount_amount: 1.5, lines: [{ regular_shopper_price: 3.65, planned_volume_units: 4_000 }, {}] })
      expect(saved).toEqual([
        { regular_shopper_price: 3.65, planned_volume_units: 4_000, total_planned_spending: 6_000, promoted_shopper_price: 2.15 },
        { total_planned_spending: undefined, promoted_shopper_price: undefined },
      ])
      expect(lines_for_save({ promotion_type: "TPR" })).toBeUndefined()
    })
  })

  describe("split_baseline_and_lift", () => {
    it("works the baseline back out of a volume that includes the lift", () => {
      expect(split_baseline_and_lift(12_000, 20)).toEqual({ baseline: 10_000, lift: 2_000 })
      expect(split_baseline_and_lift(12_000, undefined)).toBeNull()
    })
  })
}
